import { RefObject, useLayoutEffect, useRef } from 'react';
import { useDebouncedFunction } from './useDebouncedFunction';
import './useScrollPadding.module.scss';

type ReferenceElement = RefObject<HTMLElement | null>;

const stickyCssVarName = '--sticky-element-height';

const updateScrollPadding = (
  stickyElement: HTMLElement,
  paddingEl: HTMLElement,
  offset: number
) => {
  const stickyElementHeight = stickyElement.getBoundingClientRect().height;
  const desiredPadding = `${stickyElementHeight + offset}px`;

  // Allow other elements on the page to reference the sticky elements height
  paddingEl.style.setProperty(stickyCssVarName, desiredPadding);

  // Only apply style if values have changed, otherwise the browser may unexpectedly scroll
  if (paddingEl.style.scrollPaddingTop !== desiredPadding) {
    paddingEl.style.scrollPaddingTop = desiredPadding;
  }
};

export const useScrollPadding = (
  stickyElement: ReferenceElement,
  paddingTarget?: ReferenceElement,
  options: { debounceTime: number; offset: number } = {
    debounceTime: 1000,
    offset: 20,
  }
) => {
  // Store default as a ref, otherwise react will see it as a new element each render
  const defaultTargetRef = useRef(document.documentElement);
  const paddingTargetRef = paddingTarget ?? defaultTargetRef;
  const debouncedUpdate = useDebouncedFunction(
    updateScrollPadding,
    options.debounceTime
  );
  useLayoutEffect(() => {
    const stickyEl = stickyElement.current;
    const paddingEl = paddingTargetRef.current;

    // Don't add observer if we're missing an element
    if (!stickyEl || !paddingEl) return;

    stickyEl.setAttribute('data-sticky', '');

    const observer = new ResizeObserver(() =>
      debouncedUpdate(stickyEl, paddingEl, options.offset)
    );
    observer.observe(stickyEl);

    return () => {
      observer.disconnect();
      // Clear any styles/attributes that were added
      paddingEl.style.scrollPaddingTop = '';
      paddingEl.style.removeProperty(stickyCssVarName);
      stickyEl.removeAttribute('data-sticky');
    };
  }, [stickyElement, paddingTarget]);
};
